import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, Signal, effect, signal } from '@angular/core';
import { ENVIRONMENT_TOKEN, Environment } from '@core/models/environment.model';
import { UserProfile } from '@core/models/user-profile.model';
import { LaunchDarklyService } from '@core/services/launchdarkly/launchdarkly.service';
import { UserStoreService } from '@core/services/user-store/user-store.service';
import { KEYCLOAK_EVENT_SIGNAL, KeycloakEvent, KeycloakEventType } from 'keycloak-angular';
import Keycloak, { KeycloakTokenParsed } from 'keycloak-js';
import { BehaviorSubject, Observable, Subscription, from, interval, of } from 'rxjs';
import { finalize, map, mergeMap } from 'rxjs/operators';

export enum AuthLogoutEventType {
  KEYCLOACK = 'KeycloakEventType.OnAuthLogout',
  USER = 'KeycloakEventType.OnUserTriggeredLogout'
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private static readonly TOKEN_REFRESH_TIME_INTERVAL_MS: number = 240000; // 4 minutes
  private static readonly FMP_AUTH_RETRY_COUNT: number = 3;

  private keycloakParsedToken: KeycloakTokenParsed | undefined = {};
  private isInitializingUserSession = false;

  private isRefreshing = false;
  public fdGwToken = '';

  private userProfileSub = new Subscription();
  private tokenRefreshSub = new Subscription();

  private fdGwToken$: BehaviorSubject<string> = new BehaviorSubject(null);

  public getFdGwTokenAsObservable: Observable<string> = this.fdGwToken$.asObservable();
  public token = signal<string>(null);

  constructor(
    private keycloak: Keycloak,
    private httpClient: HttpClient,
    private launchDarklyService: LaunchDarklyService,
    private userService: UserStoreService,
    @Inject(ENVIRONMENT_TOKEN) private environment: Environment,
    @Inject(KEYCLOAK_EVENT_SIGNAL) private keycloackSignal: Signal<KeycloakEvent>
  ) {
    effect(() => {
      const event = this.keycloackSignal();
      this.handleKeycloakEvent(event);
    });
  }

  /**
   * boolean flag indicating if Keycloak token is expired or not
   *
   * @returns boolean flag indicating if Keycloak token is expired or not
   * @description If keycloak is not initialized then this function will return false!
   */
  public isKeycloakTokenExpired(): boolean {
    if (this.keycloakParsedToken == null) {
      return false;
    }
    try {
      const tokenExpiryTimeEpoch = this.keycloakParsedToken.exp || -1;
      const expireInSeconds = Math.round(tokenExpiryTimeEpoch - new Date().getTime() / 1000);
      return expireInSeconds <= 0 ? true : false;
    } catch (error) {
      // failed to get token expiry
      return false;
    }
  }

  /** Handling different Keycloak events
   ** OnAuthError = 0,
   ** OnAuthLogout = 1,
   ** OnAuthRefreshError = 2,
   ** OnAuthRefreshSuccess = 3,
   ** OnAuthSuccess = 4,
   ** OnReady = 5,
   ** OnTokenExpired = 6
   */
  public handleKeycloakEvent(event: KeycloakEvent): void {
    /** ---------------------OnAuthSuccess------------------------ */
    const isReadyAndAuthenticated = event.type === KeycloakEventType.Ready && event.args === true;
    if (event.type === KeycloakEventType.AuthSuccess || isReadyAndAuthenticated) {
      // auth successful
      this.onSuccessfulKeycloakLogin();
    }

    /** ---------------------OnTokenExpired--------------------------- */
    if (event.type === KeycloakEventType.TokenExpired) {
      // token expired, needs to be refreshed
      this.refreshToken();
    }

    /** ---------------------.OnAuthLogout------------------------------ */
    if (event.type === KeycloakEventType.AuthLogout) {
      // logout triggered
      this.logout(AuthLogoutEventType.KEYCLOACK).subscribe();
    }

    /** ---------------------OnAuthError---------------------------------- */
    if (event.type === KeycloakEventType.AuthError) {
      // authentication error, logging out
      console.error('KeycloakEventType.AuthError', event.args);
    }

    /** ---------------------OnAuthRefreshError------------------------------ */
    if (event.type === KeycloakEventType.AuthRefreshError) {
      // authentication refresh error, logging out
      console.error('KeycloakEventType.OnAuthRefreshError', event.args);
    }
  }

  /** Refresh the Keycloak token and create a new FMP token */
  public async refreshToken(): Promise<void> {
    // Prevent overlapping refresh calls (timer + Keycloak expiry event) to avoid duplicate token updates.
    if (!this.userService.isAuthenticated || this.isRefreshing) {
      return;
    }

    const token = this.keycloak.token;

    if (!token) {
      console.warn('[AuthService] Skip refresh: missing Keycloak token');
      return;
    }

    this.isRefreshing = true;
    try {
      this.keycloakParsedToken = this.keycloak.tokenParsed;
      // -1 to force refresh the token
      await this.keycloak.updateToken(-1);
      this.emitToken();

      // dispatch the new token to the navbar
      document.dispatchEvent(new CustomEvent('navbar_RefreshToken', { detail: this.fdGwToken }));
    } catch (err) {
      console.error(`Error in refreshing the token: ${err}`);
    } finally {
      this.isRefreshing = false;
    }
  }

  /** logout the user*/
  public logout(eventType: AuthLogoutEventType): Observable<void> {
    // if the logout event is triggered by the user, clear local storage and session storage
    // no need to reset in-app stores because the user will be redirected back to myscania
    if (eventType === AuthLogoutEventType.USER) {
      window.sessionStorage.clear();
      window.localStorage.clear();
    }

    if (!this.keycloakParsedToken) {
      // cannot perform logout because there is no token
      return of(null);
    }

    return from(this.keycloak.logout({ redirectUri: this.environment.myScaniaURL }));
  }

  /**  method invoked after successful keycloak login */
  private onSuccessfulKeycloakLogin(): void {
    if (this.userService.isAuthenticated || this.isInitializingUserSession) {
      return;
    }

    this.isInitializingUserSession = true;
    this.keycloakParsedToken = this.keycloak.tokenParsed;

    this.emitToken();
    this.scheduleTokenRefresh();

    this.userProfileSub.unsubscribe();
    this.userProfileSub = this.getUserProfile()
      .pipe(
        mergeMap((userProfile) => this.launchDarklyService.init(userProfile).pipe(map(() => ({ userProfile })))),
        finalize(() => (this.isInitializingUserSession = false))
      )
      .subscribe({
        next: ({ userProfile }) => {
          this.userService.profile$.next(userProfile);
          this.userService.isAuthenticated$.next(true);
        },
        error: (error) => console.error('Failed to create FMP token. Reason: ' + JSON.stringify(error))
      });
  }

  /** create the Federated Gateway Token which will be used in the future
   * and the FMP Token which is currently used for our services */
  private emitToken(): void {
    if (!this.keycloak.token) {
      throw new Error('Keycloak token is not available');
    }

    this.fdGwToken = this.keycloak.token;
    this.token.set(this.fdGwToken);
    this.fdGwToken$.next(this.fdGwToken);
  }

  /** get the current user profile */
  private getUserProfile(): Observable<UserProfile> {
    const profileUrl = `${this.environment.facadeBaseAws}/api/v1/profile`;

    return this.httpClient.get<UserProfile>(profileUrl);
  }

  /**
   * Schedule the Refresh token timer
   */
  private scheduleTokenRefresh(): void {
    this.tokenRefreshSub.unsubscribe();
    this.tokenRefreshSub = interval(AuthService.TOKEN_REFRESH_TIME_INTERVAL_MS).subscribe(() => this.refreshToken());
  }
}

export interface FmpTokenResponse {
  access_token: string;
}

/**  Logout event payload that is sent to Auth API */
export interface LogoutEventPayload {
  sessionState: string;
  clientId: string;
  reason: string;
}
